import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from 'apollo-client'
import { GraphQLError, UserError, ValidationError } from 'helpers'

import candleSubscriptionQuery, { type CandleSubscriptionVariables } from './graph/candleSubscription.graphql'
import caseSubscriptionQuery, { type CaseSubscriptionVariables } from './graph/caseSubscription.graphql'
import subscriptionSettingsUpdateQuery, { type SubscriptionSettingsUpdateVariables } from './graph/subscriptionSettingsUpdate.graphql'

// TODO remove if ft cashbirdSubscriptionSettings enabled on prod - added on 21.04.2022 by sonatskiy
import caseSubscriptionUpdateQueryOld from './graph/caseSubscriptionUpdate.graphql'


type UseCaseSubscriptionInput = {
  withProduct?: CaseSubscriptionVariables['withProduct']
}

export const useCaseSubscription = ({ withProduct = false }: UseCaseSubscriptionInput = {}) => {
  const { data, isFetching, error } = useQuery(caseSubscriptionQuery, {
    variables: {
      withProduct,
    },
    fetchPolicy: 'cache-first',
  })

  return {
    caseSubscription: data?.currentUser?.data?.caseSubscription,
    isFetching,
    error,
  }
}

type UseCandleSubscriptionInput = {
  withProduct?: CandleSubscriptionVariables['withProduct']
}

export const useCandleSubscription = ({ withProduct = false }: UseCandleSubscriptionInput = {}) => {
  const { data, isFetching, error } = useQuery(candleSubscriptionQuery, {
    variables: {
      withProduct,
    },
    fetchPolicy: 'cache-first',
  })

  const candleSubscription = useMemo(() => {
    const rawData = data?.currentUser?.data?.candleSubscription

    if (!rawData) {
      return null
    }

    return {
      ...rawData,
      price: rawData.price || 2500, // ATTN price on backend is taken from current candle of the month, this fallback for corner-case if CotM isn't defined yet
    }
  }, [ data ])

  return {
    candleSubscription,
    isFetching,
    error,
  }
}


type UseSubscriptionSettingsUpdateProps = {
  type: 'candle' | 'case'
  withProduct?: boolean
  toEnable: boolean
  metadata: SubscriptionSettingsUpdateVariables['input']['metadata']
}

export const useCaseSubscriptionUpdateOld = () => {
  const [ mutate, { isFetching } ] = useMutation(caseSubscriptionUpdateQueryOld)

  const submit = useCallback(async (props: UseSubscriptionSettingsUpdateProps) => {
    const { withProduct = false, toEnable, metadata } = props

    const result = await mutate({
      variables: {
        input: {
          enabled: toEnable,
          metadata,
        },
        withProduct,
      },
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { data, error } = result.data.caseSubscriptionUpdate

    if (error) {
      throw new UserError(error, error.message)
    }

    return data
  }, [ mutate ])

  return [
    submit,
    { isFetching },
  ] as const
}

export const useSubscriptionSettingsUpdate = () => {
  const [ mutate, { isFetching } ] = useMutation(subscriptionSettingsUpdateQuery)

  const submit = useCallback(async (props: UseSubscriptionSettingsUpdateProps) => {
    const { withProduct = false, type, toEnable, metadata } = props

    const isCase = type === 'case'

    const result = await mutate({
      variables: {
        input: {
          [type]: toEnable ? 'ENABLED' : 'DISABLED',
          metadata,
        },
        isCase,
        withProduct,
      },
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { data, error } = result.data.subscriptionSettingsUpdate

    if (error) {
      if (error.__typename === 'ValidationError') {
        throw new ValidationError(error)
      }

      throw new UserError(error, error.message)
    }

    return data
  }, [ mutate ])

  return [
    submit,
    { isFetching },
  ] as const
}
